import "./Resources.css";
import { Button, Card, Spinner, useAuth } from "@adafia/ui";
import { ExternalLink, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useResources } from "../hooks/useResources";
import type { Resource } from "../types";
import { getResourceIcon, getResourceLabel } from "../utils/resourceHelpers";
import { DeleteConfirmationModal } from "./Resources/DeleteConfirmationModal";
import { ResourceForm } from "./Resources/ResourceForm";

type ResourcesProps = object;

const Resources: React.FC<ResourcesProps> = () => {
	const { isAuthenticated } = useAuth();
	const { resourceRequest, createResource, updateResource, deleteResource } =
		useResources();
	const [isFormOpen, setIsFormOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [editingResource, setEditingResource] = useState<
		Resource | undefined
	>();
	const [deletingResource, setDeletingResource] = useState<
		Resource | undefined
	>();

	const handleAdd = () => {
		setEditingResource(undefined);
		setIsFormOpen(true);
	};

	const handleEdit = (resource: Resource) => {
		setEditingResource(resource);
		setIsFormOpen(true);
	};

	const handleDeleteClick = (resource: Resource) => {
		setDeletingResource(resource);
		setIsDeleteModalOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (deletingResource?.id !== undefined) {
			try {
				await deleteResource(deletingResource.id);
				setIsDeleteModalOpen(false);
			} catch (error) {
				console.error("Failed to delete resource:", error);
			}
		}
	};

	const handleSave = async (
		resourceData: Omit<Resource, "id" | "createdAt">,
	) => {
		if (editingResource) {
			if (editingResource.id !== undefined) {
				await updateResource({
					id: editingResource.id,
					resource: resourceData,
				});
			}
		} else {
			await createResource(resourceData);
		}
	};

	if (resourceRequest.status === "loading") {
		return (
			<section id="resources">
				<div className="spinner-container">
					<Spinner size="large" />
				</div>
			</section>
		);
	}

	const data = resourceRequest.data || [];

	return (
		<div className="resources-wrapper">
			{isAuthenticated && (
				<div className="resources-actions">
					<Button onClick={handleAdd} className="add-resource-btn">
						<Plus size={18} /> Add Resource
					</Button>
				</div>
			)}

			<div className="info-box">
				<p>
					A collection of conference talks, technical articles, and occasional
					posts authored by me or people I learn from.
				</p>
			</div>

			<div className="resources-grid">
				{data.map((resource) => (
					<Card key={resource.id} className="resource-card">
						<div className="card-header">
							<div className="card-icon-box" aria-hidden="true">
								{getResourceIcon(resource.type)}
							</div>
							<div className="card-meta">
								<span className="meta-tag">{resource.source}</span>
								<div className="card-actions">
									<a
										href={resource.link}
										className="action-btn"
										target="_blank"
										rel="noopener noreferrer"
										title="Open link"
										aria-label={`Open ${resource.title} in new tab`}
									>
										<ExternalLink size={18} />
									</a>
									{isAuthenticated && (
										<>
											<button
												type="button"
												onClick={() => handleEdit(resource)}
												className="action-btn edit-btn"
												title="Edit resource"
												aria-label={`Edit ${resource.title}`}
											>
												<Pencil size={16} />
											</button>
											<button
												type="button"
												onClick={() => handleDeleteClick(resource)}
												className="action-btn delete-btn"
												title="Delete resource"
												aria-label={`Delete ${resource.title}`}
											>
												<Trash2 size={16} />
											</button>
										</>
									)}
								</div>
							</div>
						</div>

						<span className="card-type">{getResourceLabel(resource.type)}</span>

						<h3 className="card-title">
							<a href={resource.link} target="_blank" rel="noopener noreferrer">
								{resource.title}
							</a>
						</h3>

						<p className="card-desc">{resource.description}</p>
					</Card>
				))}
			</div>

			<ResourceForm
				key={editingResource?.id || "new"}
				resource={editingResource}
				isOpen={isFormOpen}
				onClose={() => setIsFormOpen(false)}
				onSave={handleSave}
			/>

			{deletingResource && (
				<DeleteConfirmationModal
					isOpen={isDeleteModalOpen}
					onClose={() => setIsDeleteModalOpen(false)}
					onConfirm={handleConfirmDelete}
					title={deletingResource.title}
				/>
			)}
		</div>
	);
};

export default Resources;
